/**
 * PatientPaymentMethodsBlock Component
 *
 * Collapsible panel for managing patient payment methods through Stripe Connect.
 * Only visible when the provider has Stripe Connect enabled.
 */

import { useCallback, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { Add as AddIcon, CreditCard as CreditCardIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import CollapsibleBaseBlock from '@components/Patient/Blocks/CollapsibleBaseBlock';
import { useEventTracking } from '@services/statsig';

import { usePatientPaymentMethods } from '../store/hooks';

import { AddPatientPaymentMethodModal } from './AddPatientPaymentMethodModal';
import { PatientPaymentMethodList } from './PatientPaymentMethodList';

export interface PatientPaymentMethodsBlockProps {
  patientId: string;
  defaultCollapsed?: boolean;
  /** When true, shows the block in preview mode even if Stripe is not enabled */
  previewMode?: boolean;
}

/**
 * Main collapsible block for patient payment methods
 * Only renders when Stripe Connect is enabled for the provider
 */
export function PatientPaymentMethodsBlock({
  patientId,
  defaultCollapsed = true,
  previewMode = false,
}: PatientPaymentMethodsBlockProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { patientPaymentMethod } = useEventTracking();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasLoadedData, setHasLoadedData] = useState(!defaultCollapsed);

  const {
    paymentMethods,
    isStripeEnabled,
    isLoading,
    isSettingDefault,
    isDeleting,
    error,
    deleteMethod,
    setDefault,
    refetch,
  } = usePatientPaymentMethods(patientId);

  // Track panel expand
  const handleCollapseChange = useCallback(
    (isCollapsed: boolean) => {
      if (!isCollapsed && !hasLoadedData) {
        setHasLoadedData(true);
        refetch?.();
      }

      if (!isCollapsed) {
        patientPaymentMethod?.panelExpanded(patientId, {
          payment_methods_count: paymentMethods.length,
        });
      }
    },
    [hasLoadedData, patientId, paymentMethods.length, patientPaymentMethod, refetch],
  );

  // Handle add button click
  const handleAddClick = useCallback(() => {
    patientPaymentMethod?.addClicked(patientId);
    setIsModalOpen(true);
  }, [patientId, patientPaymentMethod]);

  // Handle add success
  const handleAddSuccess = useCallback(() => {
    patientPaymentMethod?.added(patientId);
    enqueueSnackbar('Payment method added successfully', { variant: 'success' });
  }, [patientId, patientPaymentMethod, enqueueSnackbar]);

  // Handle delete
  const handleDelete = useCallback(
    async (paymentMethodId: string) => {
      try {
        await deleteMethod(paymentMethodId);
        patientPaymentMethod?.deleted(patientId, { payment_method_id: paymentMethodId });
        enqueueSnackbar('Payment method removed', { variant: 'success' });
      } catch (err) {
        patientPaymentMethod?.deleteFailed(patientId, {
          payment_method_id: paymentMethodId,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
        enqueueSnackbar('Failed to remove payment method', { variant: 'error' });
      }
    },
    [deleteMethod, patientId, patientPaymentMethod, enqueueSnackbar],
  );

  // Handle set default
  const handleSetDefault = useCallback(
    async (paymentMethodId: string) => {
      try {
        await setDefault(paymentMethodId);
        patientPaymentMethod?.setDefault(patientId, { payment_method_id: paymentMethodId });
        enqueueSnackbar('Default payment method updated', { variant: 'success' });
      } catch (err) {
        enqueueSnackbar('Failed to update default payment method', { variant: 'error' });
      }
    },
    [setDefault, patientId, patientPaymentMethod, enqueueSnackbar],
  );

  // Don't render if Stripe Connect is not enabled (unless in preview mode)
  if (!isStripeEnabled && !previewMode) {
    return null;
  }

  // In preview mode without Stripe enabled, show a preview state
  const isPreviewState = previewMode && !isStripeEnabled;

  return (
    <>
      <CollapsibleBaseBlock
        defaultCollapsed={defaultCollapsed}
        onCollapseChange={handleCollapseChange}
      >
        <CollapsibleBaseBlock.Header
          title="Payment Methods"
          icon={<CreditCardIcon sx={{ fontSize: 20, color: 'text.secondary' }} />}
          loading={isLoading && !isPreviewState}
          action={
            !isPreviewState ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddClick}
                disabled={isLoading}
                sx={{
                  borderRadius: '0.5rem',
                  textTransform: 'none',
                  borderColor: 'borderColors.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                Add
              </Button>
            ) : undefined
          }
        />

        <CollapsibleBaseBlock.Body>
          {isPreviewState ? (
            <Box
              sx={{
                py: 3,
                textAlign: 'center',
                color: 'text.secondary',
              }}
            >
              No payment methods on file
            </Box>
          ) : isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load payment methods. Please try again.
            </Alert>
          ) : (
            <PatientPaymentMethodList
              paymentMethods={paymentMethods}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              onAddClick={handleAddClick}
              isSettingDefault={isSettingDefault}
              isDeleting={isDeleting}
            />
          )}
        </CollapsibleBaseBlock.Body>
      </CollapsibleBaseBlock>

      {!isPreviewState && (
        <AddPatientPaymentMethodModal
          open={isModalOpen}
          patientId={patientId}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleAddSuccess}
        />
      )}
    </>
  );
}

export default PatientPaymentMethodsBlock;
